import { format } from 'date-fns'
import html2canvas from 'html2canvas'

import { getSectionsScreenshots } from '../../../../features/components/PdfGeneration/generatePdfReport'
import generatePdfReport from '../../../../features/components/PdfGeneration/generatePdfReport'
import { cleanUpTemporaryPDFElements } from '../../../../helpers/pdf'
import { getAppDomainName } from '../../../../features/helpers/getAppDomainName'

import { EXPORT_CHART_ICON_ID } from '../constants'

// Function to get chart sections for PDF (similar to getReportPDFSections)
const getChartPDFSections = async chartId => {
  const chartElement = document.querySelector(`#${chartId}`)

  if (!chartElement) {
    console.error(`Chart element with ID "${chartId}" not found`)
    return []
  }

  // Temporarily hide the download icon during screenshot
  const downloadIcon = chartElement.querySelector(`#${EXPORT_CHART_ICON_ID}`)
  if (downloadIcon) {
    downloadIcon.classList.add('pdf-hidden')
  }

  const sections = [chartElement]

  // Create screenshots for the chart section
  const content = await getSectionsScreenshots(sections)

  // Restore the download icon visibility
  if (downloadIcon) {
    downloadIcon.classList.remove('pdf-hidden')
  }

  // Clean up all temporary elements with ".temporary-pdf-wrapper" class
  cleanUpTemporaryPDFElements()

  return content
}

// Export individual chart as PNG
export const exportChartAsPng = async ({ chartId, title, onExportStart, onExportEnd }) => {
  try {
    // For charts that support callbacks, wait for them to signal ready
    // For simple charts without callbacks, skip waiting and capture immediately
    if (onExportStart) {
      const chartReadyPromise = new Promise(resolve => {
        onExportStart(resolve)
      })
      await chartReadyPromise
    }

    // For charts that have export copies, capture those instead of the grid version
    // Check in the full-width container first, then grid containers for export copies (with -export suffix)
    let chartElement = document.querySelector(`#${chartId}-export`)

    // Fallback to regular query if export copy not found
    if (!chartElement) {
      chartElement = document.querySelector(`#${chartId}`)
    }

    if (!chartElement) {
      console.error(`Chart element with ID "${chartId}" not found`)
      if (onExportEnd) {
        onExportEnd()
      }
      return
    }

    // Temporarily hide the download icon during screenshot
    const downloadIcon = chartElement.querySelector(`#${EXPORT_CHART_ICON_ID}`)
    if (downloadIcon) {
      downloadIcon.style.display = 'none'
    }

    try {
      // Use html2canvas to capture the entire chart container including title, legend, etc.
      const canvas = await html2canvas(chartElement, {
        backgroundColor: '#ffffff',
        scale: 2, // Higher quality (2x resolution)
        logging: false,
        useCORS: true
      })

      // Exit export mode to restore normal layout
      if (onExportEnd) {
        onExportEnd()
      }

      // Convert canvas to blob
      canvas.toBlob(blob => {
        if (!blob) {
          console.error('Failed to create image blob')
          return
        }

        // Create download link
        const url = URL.createObjectURL(blob)
        const currentDate = format(new Date(), 'dd-MMM-yyyy')
        const fileName = `SROI - ${title} - ${currentDate}.png`

        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // Clean up the object URL
        URL.revokeObjectURL(url)
      }, 'image/png')
    } finally {
      // Always restore the download icon visibility, even if an error occurs
      if (downloadIcon) {
        downloadIcon.style.display = ''
      }
    }
  } catch (error) {
    console.error('Error exporting chart as PNG:', error)
    // Ensure we exit export mode even if there's an error
    if (onExportEnd) {
      onExportEnd()
    }
  }
}

export const exportSingleChart = async ({ chartId, title }) => {
  try {
    const contentSections = await getChartPDFSections(chartId)

    if (contentSections.length === 0) {
      console.error('No content sections found for chart export')
      return
    }

    const currentDate = format(new Date(), 'dd-MMM-yyyy')
    const fileName = `SROI - ${title} - ${currentDate}`
    const appName = getAppDomainName()

    await generatePdfReport({
      title: `SROI: ${title}`,
      contentSections,
      fileName,
      appName
    })
  } catch (error) {
    console.error('Error exporting chart:', error)
    cleanUpTemporaryPDFElements()
  }
}
